import React, { useState, useEffect } from 'react';
import { getChildAssignments, deleteAssignment } from '../../services/auth';

interface AssignmentsListProps {
  studentId: string;
  refreshTrigger?: number;
}

export const AssignmentsList: React.FC<AssignmentsListProps> = ({ studentId, refreshTrigger }) => {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAssignments = () => {
    setLoading(true);
    getChildAssignments(studentId)
      .then(setAssignments)
      .catch(() => setAssignments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAssignments();
  }, [studentId, refreshTrigger]);

  const handleDelete = async (assignmentId: string) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await deleteAssignment(studentId, assignmentId);
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
    } catch (err) {
      console.error('Failed to delete assignment:', err);
    }
  };

  if (loading) return <div className="panel-loading">Loading assignments...</div>;
  if (assignments.length === 0) {
    return <div className="panel-empty">No assignments created yet.</div>;
  }

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'badge-pending';
      case 'in_progress': return 'badge-progress';
      case 'completed': return 'badge-completed';
      case 'expired': return 'badge-expired';
      default: return '';
    }
  };

  return (
    <div className="assignments-list">
      <table className="assignments-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Unit/Lesson</th>
            <th>Progress</th>
            <th>Due</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((a) => (
            <tr key={a.id}>
              <td className="assignment-title">{a.title}</td>
              <td>
                Unit {a.target_unit}
                {a.target_lesson && `, Lesson ${a.target_lesson}`}
              </td>
              <td>
                {a.questions_completed}/{a.num_questions}
                {a.questions_completed > 0 && (
                  <span className="accuracy-hint">
                    {' '}({Math.round((a.questions_correct / a.questions_completed) * 100)}%)
                  </span>
                )}
              </td>
              <td>
                {a.due_date
                  ? new Date(a.due_date).toLocaleDateString()
                  : '-'}
              </td>
              <td>
                <span className={`status-badge ${statusBadgeClass(a.status)}`}>
                  {a.status.replace('_', ' ')}
                </span>
              </td>
              <td>
                {(a.status === 'pending' || a.status === 'in_progress') && (
                  <button
                    className="delete-btn-small"
                    onClick={() => handleDelete(a.id)}
                    title="Delete assignment"
                  >
                    &times;
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
